import React, { useState } from 'react';
import { styles } from './adminStyles';
import GlobalEligibilityPanel from './GlobalEligibilityPanel';

const LAYER_TYPE_LABELS = { eligibility: 'Global Eligibility', priority: 'Event Focus Area' };

const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

// Uploads save straight to the library (no auction/global scope chosen
// here) — the admin applies the saved layer afterwards from the relevant
// auction's "Select a Focus Area" dropdown, or the Global Eligibility panel.
const UploadToLibrarySection = ({ onUpload }) => {
  const [file, setFile] = useState(null);
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [layerType, setLayerType] = useState('priority');
  const [isBusy, setIsBusy] = useState(false);
  const [status, setStatus] = useState('');

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file || !name) return;
    setIsBusy(true); setStatus('');
    try {
      await onUpload({ file, name, notes, layerType });
      setStatus('✅ Layer Added to Library!'); setFile(null); setName(''); setNotes('');
      setTimeout(() => setStatus(''), 3000);
    } catch (err) {
      setStatus('❌ Upload Failed: ' + (err.response?.data?.msg || 'Error'));
    } finally { setIsBusy(false); }
  };

  return (
    <form onSubmit={handleUpload} style={{ ...styles.card, marginBottom: '20px', display: 'grid', gridTemplateColumns: '200px 1fr 1fr auto', gap: '15px', alignItems: 'start' }}>
      <div>
        <label style={styles.label}>Layer Type</label>
        <select disabled={isBusy} style={{ ...styles.select, marginBottom: 0 }} value={layerType} onChange={(e) => setLayerType(e.target.value)}>
          <option value="priority">Event Focus Area</option>
          <option value="eligibility">Global Eligibility</option>
        </select>
      </div>
      <div>
        <label style={styles.label}>Layer Name</label>
        <input disabled={isBusy} type="text" placeholder="Layer Name" value={name} onChange={(e) => setName(e.target.value)} style={{ ...styles.input, marginBottom: 0 }} required />
      </div>
      <div>
        <label style={styles.label}>GeoJSON File</label>
        <input disabled={isBusy} type="file" accept=".json,.geojson" onChange={(e) => setFile(e.target.files[0])} style={{ width: '100%', fontFamily: '"Inter", sans-serif', fontSize: '0.85rem', padding: '10px 0' }} />
      </div>
      <div>
        <label style={{ ...styles.label, opacity: 0 }}>Upload</label>
        <button disabled={isBusy || !file || !name} type="submit" style={{ ...styles.btnPrimary, opacity: (isBusy || !file || !name) ? 0.6 : 1, cursor: (isBusy || !file || !name) ? 'not-allowed' : 'pointer' }}>
          {isBusy ? 'Uploading...' : 'Upload Layer'}
        </button>
      </div>
      <textarea disabled={isBusy} placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} style={{ ...styles.input, gridColumn: '2 / 4', height: '50px', resize: 'vertical', marginBottom: 0 }} />
      {status && <div style={{ gridColumn: '1 / -1', fontSize: '0.85rem', fontWeight: 'bold', color: status.includes('Failed') ? '#F59E0B' : '#0D9488' }}>{status}</div>}
    </form>
  );
};

// Admin section for inspecting every GeoJSON layer ever uploaded to the
// tenant's library (across both Global Eligibility and Event Focus Area),
// independent of any single auction's Zones sub-tab. Mirrors the
// Active/Archived tab pattern used by ActionLibrary.js and AlgorithmLibrary.js.
const LayerLibrary = ({
  layers, layerLibraryLoading, handleUploadToLibrary, handleArchiveToggleLayer, handleDeleteLayer,
  globalZones, eligibilityLayers, handleApplyGlobalEligibilityLayer
}) => {
  const [showArchivedView, setShowArchivedView] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');

  const visibleLayers = (layers || [])
    .filter(l => showArchivedView ? l.status === 'archived' : l.status !== 'archived')
    .filter(l => typeFilter === 'all' || l.layerType === typeFilter);

  return (
    <div>
    <UploadToLibrarySection onUpload={handleUploadToLibrary} />
    <div style={{ marginBottom: '20px' }}>
      <GlobalEligibilityPanel
        globalZones={globalZones}
        eligibilityLayers={eligibilityLayers}
        onApplyExisting={handleApplyGlobalEligibilityLayer}
      />
    </div>
    <div style={styles.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2 style={{ margin: 0, ...styles.h2 }}>Layer Library</h2>
          <p style={{ margin: 0, color: '#475569', fontSize: '0.9rem' }}>Every GeoJSON layer uploaded to your organisation's library, across all events.</p>
        </div>
        <select style={{ ...styles.select, width: '220px', marginBottom: 0 }} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="all">All Types</option>
          <option value="eligibility">Global Eligibility</option>
          <option value="priority">Event Focus Area</option>
        </select>
      </div>

      <div style={{ display: 'flex', gap: '30px', borderBottom: '2px solid #ddd', marginBottom: '20px' }}>
        <button
          onClick={() => setShowArchivedView(false)}
          style={{ background: 'none', border: 'none', borderBottom: !showArchivedView ? '3px solid #0D9488' : '3px solid transparent', color: !showArchivedView ? '#0F172A' : '#475569', fontWeight: 'bold', padding: '10px 5px', fontSize: '1rem', cursor: 'pointer', fontFamily: '"Inter", sans-serif', transform: 'translateY(2px)' }}
        >
          Active Layers
        </button>
        <button
          onClick={() => setShowArchivedView(true)}
          style={{ background: 'none', border: 'none', borderBottom: showArchivedView ? '3px solid #0D9488' : '3px solid transparent', color: showArchivedView ? '#0F172A' : '#475569', fontWeight: 'bold', padding: '10px 5px', fontSize: '1rem', cursor: 'pointer', fontFamily: '"Inter", sans-serif', transform: 'translateY(2px)' }}
        >
          Archived Layers
        </button>
      </div>

      <div style={{ overflowX: 'auto', backgroundColor: '#FFFFFF', borderRadius: '6px', border: '1px solid #E2E8F0' }}>
        <table style={{ ...styles.table, width: '100%' }}>
          <thead>
            <tr style={{ backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0' }}>
              <th style={{ ...styles.th, textAlign: 'left' }}>Name</th>
              <th style={{ ...styles.th, textAlign: 'center' }}>Type</th>
              <th style={{ ...styles.th, textAlign: 'center' }}>Uploaded</th>
              <th style={{ ...styles.th, textAlign: 'center' }}>Status</th>
              <th style={{ ...styles.th, textAlign: 'left' }}>Active Usage</th>
              <th style={{ ...styles.th, textAlign: 'center' }}>Update</th>
            </tr>
          </thead>
          <tbody>
            {layerLibraryLoading && (
              <tr><td colSpan="6" style={{ padding: '20px', textAlign: 'center', color: '#64748B' }}>Loading layer library...</td></tr>
            )}
            {!layerLibraryLoading && visibleLayers.map(layer => (
              <tr key={layer._id} style={{ borderBottom: '1px solid #E2E8F0' }}>
                <td style={{ ...styles.td, fontWeight: 'bold', color: '#0F172A', textAlign: 'left' }}>{layer.name}</td>
                <td style={{ ...styles.td, textAlign: 'center' }}>{LAYER_TYPE_LABELS[layer.layerType] || layer.layerType}</td>
                <td style={{ ...styles.td, textAlign: 'center' }}>{formatDate(layer.createdAt)}</td>
                <td style={{ ...styles.td, textAlign: 'center' }}>
                  <span style={{ backgroundColor: layer.status === 'archived' ? '#F1F5F9' : '#E0F2FE', color: layer.status === 'archived' ? '#475569' : '#0369A1', padding: '4px 8px', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 'bold' }}>
                    {(layer.status || 'active').toUpperCase()}
                  </span>
                </td>
                <td style={{ ...styles.td, textAlign: 'left', fontSize: '0.85rem', color: '#475569' }}>
                  {(layer.activeUsage && layer.activeUsage.length > 0)
                    ? layer.activeUsage.join(', ')
                    : <span style={{ fontStyle: 'italic', color: '#94A3B8' }}>Unused</span>}
                </td>
                <td style={{ ...styles.td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                  {layer.status === 'archived' ? (
                    <button
                      onClick={() => handleArchiveToggleLayer(layer._id, false)}
                      style={{ padding: '6px 12px', border: 'none', backgroundColor: '#0D9488', color: '#FFFFFF', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem', marginRight: '10px' }}
                    >
                      Restore
                    </button>
                  ) : (
                    <button
                      onClick={() => handleArchiveToggleLayer(layer._id, true)}
                      style={{ padding: '6px 12px', border: '1px solid #0D9488', backgroundColor: '#FFFFFF', color: '#0D9488', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem', marginRight: '10px' }}
                    >
                      Archive
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (window.confirm(`Permanently delete "${layer.name}"? This cannot be undone.`)) {
                        handleDeleteLayer(layer._id);
                      }
                    }}
                    style={{ padding: '6px 12px', border: '1px solid #DC2626', backgroundColor: '#FFFFFF', color: '#DC2626', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem' }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {!layerLibraryLoading && visibleLayers.length === 0 && (
              <tr><td colSpan="6" style={{ padding: '20px', textAlign: 'center', color: '#64748B' }}>
                {showArchivedView ? 'No archived layers.' : 'No layers found in the library.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
    </div>
  );
};

export default LayerLibrary;
